import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AuthShell } from "@/components/auth/AuthShell";
import { AuthField, AuthSubmit } from "@/components/auth/AuthField";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/utils/api";
import { Mail, Lock, ArrowRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { MfaLoginStep } from "@/components/auth/MfaLoginStep";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Ensure Cloudflare's Turnstile script is present exactly once. */
function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector(`script[src^="${TURNSTILE_SCRIPT_SRC.split("?")[0]}"]`);
  if (existing) {
    return new Promise((resolve) => {
      if (window.turnstile) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = TURNSTILE_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Cloudflare Turnstile"));
    document.head.appendChild(s);
  });
}

export default function LoginPage() {
  useDocumentTitle("Sign in", "Internal staff portal — accounts are issued by invitation only.");
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [mfaToken, setMfaToken] = useState("");

  const widgetContainerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Proactive cold-start warm-up: as soon as the login page mounts, ping
  // `/api/health` so the CF↔origin TCP connection is live by the time
  // the user submits credentials.
  useEffect(() => {
    api.get("/health", { _silent: true }).catch(() => { /* silent probe */ });
  }, []);

  // Mount the Turnstile widget. Managed mode = Cloudflare decides whether
  // to show a challenge; most legit users get an invisible pass.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return; // Turnstile not configured — skip.
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !widgetContainerRef.current) return;
        if (widgetIdRef.current !== null) return; // already rendered
        widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: "login",
          theme: "light",
          callback: (token) => setTurnstileToken(token),
          "error-callback": () => setTurnstileToken(""),
          "expired-callback": () => setTurnstileToken(""),
          "timeout-callback": () => setTurnstileToken(""),
        });
      })
      .catch(() => { /* script blocked — form will fail closed with a friendly error */ });
    return () => {
      cancelled = true;
      // Best-effort cleanup on unmount to avoid orphaned widgets on remount.
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken("");
    if (window.turnstile && widgetIdRef.current !== null) {
      try { window.turnstile.reset(widgetIdRef.current); } catch { /* ignore */ }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // If Turnstile is configured but we don't have a token yet, block submit.
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the bot check just below before signing in.");
      return;
    }
    setLoading(true);
    try {
      const res = await login(email, password, turnstileToken);
      if (res?.mfa_required) setMfaToken(res.mfa_token);
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.code;
      // Turnstile tokens are single-use — reset the widget on any failure
      // so the user gets a fresh one for their next attempt.
      resetTurnstile();
      if (!status || status >= 500 || code === "ECONNABORTED") {
        setError(
          "Server didn't respond — please click Sign In again. " +
          "If the problem continues, wait a moment and try once more."
        );
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testId="login-page" portalLabel="Staff portal" title="Secure Portal Access" subtitle="Staff sign-in for training records, courses and assessments."
      switchLink={<>Are you a learner? <a href="/learn" className="font-medium text-blue-600 hover:underline" data-testid="go-learner-portal">Go to the learner portal →</a></>}
    >
      {mfaToken && <MfaLoginStep mfaToken={mfaToken} onCancel={() => { setMfaToken(""); setPassword(""); resetTurnstile(); }} />}
      <div className={mfaToken ? "hidden" : ""}>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="auth-error">{error}</div>
      )}
      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthField label="Email address" icon={Mail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required autoComplete="email" testId="login-email-input" />
        <AuthField
          label="Password" icon={Lock} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required autoComplete="current-password" testId="login-password-input"
          right={<Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>}
        />
        {TURNSTILE_SITE_KEY && <div ref={widgetContainerRef} data-testid="turnstile-widget" className="min-h-[65px]" />}
        <AuthSubmit loading={loading} disabled={!!TURNSTILE_SITE_KEY && !turnstileToken} testId="login-submit-btn">
          {loading ? "Please wait..." : <>Sign in <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
        </AuthSubmit>
      </form>
      </div>
    </AuthShell>
  );
}
